import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Howl, Howler } from "howler";

// ─── Components ───────────────────────────────────────────────────────────────
import NotificationView from "../notification/orderNotification";

// ─── Config ───────────────────────────────────────────────────────────────────
import APPConfig from "../../config";

// ─── Socket (module-level) ✅ socket.io-client v4 → io() ─────────────────────
import { io } from "socket.io-client";

const socket = io(APPConfig.SOCKET_URL, {
  transports: ["websocket", "polling", "flashsocket"],
  reconnection: true,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  reconnectionAttempts: 5,
  randomizationFactor: 0.5,
  timeout: 10000,
});

// ─── Assets (module-level) ────────────────────────────────────────────────────
import notificationAudio from "../../assets/audio/notificationAudio.mp3";

// ─── Store info (module-level) ────────────────────────────────────────────────
const storeString = localStorage.getItem("store") ?? "{}";
const storObj = JSON.parse(storeString);
const storeID = storObj?._id ?? null;

// ─── Component ────────────────────────────────────────────────────────────────
const Notification = () => {
  const navigate = useNavigate();

  // ✅ React.createRef() → useRef()
  const buttonRef = useRef<HTMLButtonElement>(null);

  const [reRun, setReRun] = useState(0);
  const [notifiPopup, setNotifiPopup] = useState(false);
  const [orderCount, setOrderCount] = useState(0);
  const [audioContextCreated, setAudioContextCreated] = useState(false);

  // ─── Effects ─────────────────────────────────────────────────────────────
  // ✅ componentDidMount → useEffect
  useEffect(() => {
    Howler.autoUnlock = true;
    Howler.html5PoolSize = 100;

    window.addEventListener("beforeunload", onUnload);

    onConnect();

    // ✅ componentWillUnmount → useEffect cleanup
    return () => {
      Howler.autoUnlock = true;
      Howler.html5PoolSize = 100;
      window.removeEventListener("beforeunload", onUnload);
    };
  }, []);

  // ✅ componentDidUpdate(prevState.reRun !== this.state.reRun) → useEffect
  useEffect(() => {
    if (reRun !== 0) {
      SoundCreate();
    }
  }, [reRun]);

  // ─── Sound handlers ───────────────────────────────────────────────────────
  const SoundStop = () => {
    Howler.unload();
  };

  const SoundCreate = async () => {
    Howler.unload();
    if (!audioContextCreated) {
      Howler.autoUnlock = true;
      Howler.mute(false);
      Howler.ctx.resume()
        .then(() => {
          setAudioContextCreated(true);
          playSound();
        })
        .catch(() => { });
    } else {
      playSound();
    }
  };

  const playSound = () => {
    const soundHowler = new Howl({
      src: [notificationAudio],
      loop: true,
      volume: 1.0,
      html5: true,
    });
    Howler.volume(1.0);
    soundHowler.once("load", () => { soundHowler.play(); });
    soundHowler.once("loaderror", () => { SoundCreate(); });
    soundHowler.once("playerror", () => { SoundCreate(); });
  };

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const onClose = () => {
    setNotifiPopup(false);
    SoundStop();
    // ✅ this.props.history.push → navigate()
    navigate("/orders");
  };

  const onUnload = (_e: any) => {
    // intentionally left — hook for beforeunload
  };

  const setNotification = () => {
    localStorage.setItem("store_notification", "1");
  };

  // const onPageReload = () => {
  //   window.location.reload();
  // };

  const onPageHandleSound = () => {
    if (buttonRef.current) {
      buttonRef.current.click();
    }
  };

  // const setReconnect = () => {
  //   socket.on(storeID + "_notification", (data: any) => {
  //     const num = Math.floor(Math.random() * 100 + 1);
  //     setReRun((prev) => prev + num);
  //     SoundCreate();
  //     setOrderCount(data);
  //     setNotifiPopup(true);
  //     setNotification();
  //   });
  // };

  // ─── Socket ───────────────────────────────────────────────────────────────
  const onConnect = () => {
    const MINUTE_MS = 300000;

    if (storeID) {
      socket.on(storeID + "_notification", (data: any) => {
        const num = Math.floor(Math.random() * 100 + 1);
        setReRun((prev) => prev + num);
        setOrderCount(data);
        setNotifiPopup(true);
        onPageHandleSound();
        setNotification();
      });

      socket.on("connect", () => { });
      socket.on("disconnect", () => { });
      socket.on("reconnect", (_attemptNumber: any) => { });
      socket.on("connect_error", (_data: any) => { });
      socket.on("reconnect_attempt", (_attemptNumber: any) => { });
      socket.on("reconnect_error", (_error: any) => { });
      socket.on("reconnect_failed", () => { });

      const interval = setInterval(() => {
        socket.emit(storeID + "_notification", "hi server");
      }, MINUTE_MS);

      // cleanup interval + socket on unmount
      return () => {
        clearInterval(interval);
        socket.disconnect();
      };
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <div id="notification_container">
        <NotificationView
          visible={notifiPopup}
          onClose={onClose}
          orderCount={orderCount}
        />
      </div>
    </>
  );
};

export default Notification;